import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useCart } from '../App';
import { ShoppingBag, Menu, X, Instagram, Youtube, Minus, Plus, Trash2 } from 'lucide-react';

const SMILEY_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_121ad1ad-d724-4658-ab95-7dc4ce7b4a83/artifacts/766c9fb51cb57f3d_image.png";

const navLinks = [
  { name: 'Fresh Drops', path: '/shop' },
  { name: 'Hoodies', path: '/shop/hoodies' },
  { name: 'Tees', path: '/shop/tshirts' },
  { name: 'Outerwear', path: '/shop/jackets' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const getSeasonalBanner = () => {
  const month = new Date().getMonth();
  const banners = {
    0:  "NEW YEAR // NEW GEAR // DROP 01 LIVE",
    1:  "BLACK HISTORY MONTH // RESILIENCE COLLECTION",
    2:  "WOMEN'S HISTORY MONTH // STRENGTH IS BEAUTIFUL",
    3:  "SPRING DROP // NEW SEASON NEW GEAR",
    4:  "MENTAL HEALTH AWARENESS // HEART ON PAUSE",
    5:  "JUNETEENTH // FREEDOM // RESILIENCE",
    6:  "SUMMER DROP // STAY CROOKID ALL SEASON",
    7:  "BACK TO SCHOOL // SHOW UP DIFFERENT",
    8:  "FALL DROP // HOODIE SEASON IS HERE",
    9:  "HALLOWEEN COLLECTION // TRICK OR TREAT",
    10: "THANKFUL FOR THE GRIND // STAY CROOKID",
    11: "HOLIDAY DROP // GIFT THE SMILE",
  };
  return banners[month];
};

const CartDrawer = ({ isOpen, onClose }) => {
  const { cart, cartTotal, cartCount, updateQuantity, removeFromCart } = useCart();

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      )}
      <div
        className={`fixed inset-y-0 right-0 max-w-md w-full bg-cs-bg2 border-l border-cs-border shadow-2xl z-50 flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between p-6 border-b border-cs-border">
          <div className="flex items-center gap-3">
            <span className="font-anton text-xl uppercase text-cs-text tracking-wider">War Chest</span>
            <span className="bg-cs-olive text-cs-text font-mono text-xs px-2 py-0.5">{cartCount}</span>
          </div>
          <button onClick={onClose} className="p-2 text-cs-muted hover:text-cs-text transition-colors" data-testid="cart-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <p className="font-mono text-cs-muted uppercase tracking-widest text-sm mb-6">No gear stashed</p>
            <Link to="/shop" onClick={onClose} className="bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-sm px-6 py-3 hover:bg-cs-olive2 transition-colors" data-testid="cart-shop-link">
              Shop Now
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {cart.map((item) => (
                <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-4 border-b border-cs-border pb-4" data-testid={`cart-item-${item.product_id}`}>
                  <img src={item.image} alt={item.name} className="w-20 h-20 object-cover border border-cs-border" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-cs-text text-sm uppercase truncate">{item.name}</p>
                    <p className="font-mono text-xs text-cs-muted mt-1">{item.size} / {item.color}</p>
                    <p className="font-mono text-cs-ochre text-sm mt-1">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity - 1)} className="p-1 border border-cs-border text-cs-muted hover:text-cs-text" data-testid={`cart-qty-minus-${item.product_id}`}>
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-mono text-cs-text text-sm w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity + 1)} className="p-1 border border-cs-border text-cs-muted hover:text-cs-text" data-testid={`cart-qty-plus-${item.product_id}`}>
                        <Plus className="w-3 h-3" />
                      </button>
                      <button onClick={() => removeFromCart(item.product_id, item.size, item.color)} className="p-1 ml-auto text-cs-alert/60 hover:text-cs-alert">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-cs-border space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-mono text-cs-muted uppercase text-sm tracking-wider">Subtotal</span>
                <span className="font-mono text-cs-ochre text-xl">${cartTotal.toFixed(2)}</span>
              </div>
              <Link
                to="/checkout"
                onClick={onClose}
                className="block w-full bg-cs-olive text-cs-text font-mono uppercase tracking-wider text-center py-4 hover:bg-cs-olive2 transition-colors"
                data-testid="cart-checkout-button"
              >
                Checkout
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { cartCount } = useCart();
  const bannerText = getSeasonalBanner();

  return (
    <>
      <header className="sticky top-0 z-40" data-testid="header">
        {/* Announcement Bar */}
        <div className="bg-cs-olive text-cs-text text-xs font-mono py-1.5 px-4 tracking-widest uppercase flex justify-center items-center border-b border-cs-border2" data-testid="seasonal-banner">
          <span>{bannerText}</span>
        </div>

        {/* Main Nav */}
        <nav className="bg-cs-bg/95 backdrop-blur-md border-b border-cs-border px-4 sm:px-8 py-3 flex items-center justify-between" data-testid="main-navigation">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
            <img src={SMILEY_LOGO} alt="Crookid Smilez" className="h-10 w-auto" />
            <span className="font-graffiti text-cs-text text-lg uppercase tracking-wider hidden sm:block">Crookid Smilez</span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className="font-mono text-xs uppercase tracking-widest text-cs-muted hover:text-cs-ochre transition-colors"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-cs-muted hover:text-cs-ochre transition-colors"
              data-testid="cart-drawer-trigger"
            >
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-cs-ochre text-cs-dark text-xs font-mono font-bold w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              className="lg:hidden p-2 text-cs-muted hover:text-cs-text transition-colors"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-cs-bg" data-testid="mobile-menu">
          <div className="flex flex-col h-full p-6">
            <div className="flex items-center justify-between mb-12">
              <img src={SMILEY_LOGO} alt="Crookid Smilez" className="h-12" />
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 border border-cs-border text-cs-muted hover:text-cs-text" data-testid="close-mobile-menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-4">
              {navLinks.map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-anton text-3xl uppercase text-cs-text hover:text-cs-ochre transition-colors tracking-wider"
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <div className="mt-auto">
              <Link
                to="/cart"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 font-anton text-xl uppercase text-cs-ochre"
                data-testid="mobile-cart-link"
              >
                <ShoppingBag className="w-6 h-6" /> Cart ({cartCount})
              </Link>
            </div>
          </div>
        </div>
      )}

      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#090A0B] border-t-2 border-cs-border text-cs-muted" data-testid="footer">
      {/* Marquee */}
      <div className="bg-cs-olive/20 border-b border-cs-border py-3 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex">
          {[...Array(10)].map((_, i) => (
            <span key={`marquee-${i}`} className="font-mono text-xs uppercase tracking-[0.3em] mx-8 text-cs-olive2">
              CROOKID SMILEZ // URBAN // RAW // DEFINED // HEART ON PAUSE COLLECTION //
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <h3 className="font-anton text-4xl lg:text-5xl uppercase text-cs-text/10 tracking-wider mb-4">
              Crookid Smilez
            </h3>
            <p className="font-mono text-xs text-cs-muted max-w-sm leading-relaxed">
              Streetwear for the resilient. Constructed under underground spec. From newborns to OG's.
            </p>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-cs-ochre mb-4">Shop</h4>
            <ul className="space-y-2 font-mono text-xs">
              <li><Link to="/shop" className="hover:text-cs-ochre transition-colors" data-testid="footer-shop">All Products</Link></li>
              <li><Link to="/shop/hoodies" className="hover:text-cs-ochre transition-colors" data-testid="footer-hoodies">Hoodies</Link></li>
              <li><Link to="/shop/tshirts" className="hover:text-cs-ochre transition-colors">Tees</Link></li>
              <li><Link to="/shop/jackets" className="hover:text-cs-ochre transition-colors">Outerwear</Link></li>
              <li><Link to="/about" className="hover:text-cs-ochre transition-colors" data-testid="footer-about">About</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-xs uppercase tracking-widest text-cs-ochre mb-4">Contact</h4>
            <ul className="space-y-2 font-mono text-xs">
              <li><a href="mailto:info@crookidsmilez.com" className="hover:text-cs-ochre transition-colors">info@crookidsmilez.com</a></li>
              <li><a href="tel:+13177594033" className="hover:text-cs-ochre transition-colors">(317) 759-4033</a></li>
              <li><Link to="/contact" className="hover:text-cs-ochre transition-colors">Contact Form</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-cs-border flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-cs-muted/50 uppercase tracking-wider">
            &copy; 2026 Crookid Smilez. All rebellions reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-2 text-cs-muted hover:text-cs-ochre transition-colors" data-testid="social-instagram">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="p-2 text-cs-muted hover:text-cs-ochre transition-colors" data-testid="social-tiktok">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
            </a>
            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="p-2 text-cs-muted hover:text-cs-ochre transition-colors" data-testid="social-youtube">
              <Youtube className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-cs-bg">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
